/**
 * EmailTab — filtered inbox per binding.
 *
 * Top: chip selector across active bindings ("Inbox · Leads · Customer replies").
 * Body: last 20 messages for the active binding.
 * Footer: "Manage filters" link → settings/calendar tab where bindings live.
 *
 * No body content — click a message → opens in the provider's webmail in a new
 * tab. The sidebar is for *triage*, not reading.
 */
import { useState } from 'react';
import { Plus, RefreshCw, Paperclip, ExternalLink, AlertCircle } from 'lucide-react';
import { clsx } from 'clsx';
import { useEmailFilters } from '../../hooks/useEmailFilters';
import { externalMessageUrl } from '../../lib/email';
import { EmailFilterPicker } from './EmailFilterPicker';
import { useAuth } from '../../contexts/AuthContext';
import { useTenant } from '../../contexts/TenantContext';

export function EmailTab() {
  const { session } = useAuth();
  const { tenant } = useTenant();
  const [pickerOpen, setPickerOpen] = useState(false);
  const {
    bindings,
    activeBindingId,
    setActiveBindingId,
    messages,
    bindingsLoading,
    messagesLoading,
    error,
    refresh,
    reloadBindings,
  } = useEmailFilters();

  const activeBinding = bindings.find((b) => b.id === activeBindingId) ?? null;

  return (
    <div className="flex h-full min-h-0 flex-col">
      <header className="flex shrink-0 items-center gap-1 border-b border-white/5 px-2 py-2">
        <div className="flex min-w-0 flex-1 items-center gap-1 overflow-x-auto">
          {bindings.length === 0 && !bindingsLoading && (
            <span className="px-1 text-[11px] text-neutral-500">No filters yet — add one to start.</span>
          )}
          {bindings.map((b) => (
            <button
              key={b.id}
              type="button"
              onClick={() => setActiveBindingId(b.id)}
              className={clsx(
                'shrink-0 rounded-full px-2.5 py-1 text-[11px] font-medium transition',
                b.id === activeBindingId
                  ? 'bg-brand-600/20 text-brand-100'
                  : 'text-neutral-400 hover:bg-white/5 hover:text-neutral-200',
              )}
              title={`${b.provider} · ${b.filter_kind}: ${b.filter_value}`}
            >
              {b.display_label}
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={() => setPickerOpen(true)}
          className="rounded-md p-1.5 text-neutral-400 transition hover:bg-white/5 hover:text-neutral-100"
          aria-label="Add filter"
          title="Add filter"
        >
          <Plus className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onClick={() => void refresh()}
          disabled={messagesLoading}
          className="rounded-md p-1.5 text-neutral-400 transition hover:bg-white/5 hover:text-neutral-100 disabled:opacity-50"
          aria-label="Refresh"
          title="Refresh"
        >
          <RefreshCw className={clsx('h-3.5 w-3.5', messagesLoading && 'animate-spin')} />
        </button>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {error && (
          <div className="m-3 flex items-start gap-2 rounded-lg border border-red-500/20 bg-red-500/[0.04] p-3 text-xs text-red-200">
            <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {!activeBinding && !bindingsLoading && (
          <div className="px-4 py-8 text-center text-xs text-neutral-500">
            Add a filter — a Gmail label or Outlook folder — to see messages
            here. Hit the <Plus className="inline h-3 w-3" /> button above.
          </div>
        )}

        {activeBinding && messages.length === 0 && !messagesLoading && (
          <div className="px-4 py-8 text-center text-xs text-neutral-500">
            No messages yet for this filter. The sync runs every 5 minutes.
          </div>
        )}

        <ul className="divide-y divide-white/5">
          {messages.map((m) => (
            <li key={m.id}>
              <a
                href={activeBinding ? externalMessageUrl(activeBinding.provider, m.external_id) : '#'}
                target="_blank"
                rel="noreferrer"
                className="group block px-3 py-2.5 transition hover:bg-white/[0.03]"
              >
                <div className="flex items-center gap-2">
                  {m.is_unread && (
                    <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-brand-400" aria-label="Unread" />
                  )}
                  <span className={clsx('flex-1 truncate text-xs', m.is_unread ? 'font-semibold text-neutral-50' : 'text-neutral-300')}>
                    {m.from_name ?? m.from_email ?? '(unknown sender)'}
                  </span>
                  <span className="shrink-0 text-[10px] text-neutral-500">{relTime(m.received_at)}</span>
                </div>
                <div className={clsx('mt-0.5 truncate text-xs', m.is_unread ? 'text-neutral-100' : 'text-neutral-400')}>
                  {m.subject ?? '(no subject)'}
                </div>
                {m.snippet && (
                  <div className="mt-0.5 line-clamp-1 text-[11px] text-neutral-500">{m.snippet}</div>
                )}
                <div className="mt-1 flex items-center gap-2 text-[10px] text-neutral-600">
                  {m.has_attachments && (
                    <span className="flex items-center gap-0.5">
                      <Paperclip className="h-3 w-3" /> attachment
                    </span>
                  )}
                  <ExternalLink className="ml-auto h-3 w-3 opacity-0 transition group-hover:opacity-100" />
                </div>
              </a>
            </li>
          ))}
        </ul>
      </div>

      {pickerOpen && session?.user && tenant && (
        <EmailFilterPicker
          tenantId={tenant.id}
          userId={session.user.id}
          onClose={() => setPickerOpen(false)}
          onCreated={() => {
            setPickerOpen(false);
            void reloadBindings();
          }}
        />
      )}
    </div>
  );
}

function relTime(iso: string): string {
  const now = Date.now();
  const t = new Date(iso).getTime();
  const diff = Math.max(0, now - t);
  const m = Math.floor(diff / 60_000);
  if (m < 1) return 'now';
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d`;
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}
